<template>
  <!-- Live market context for the selected category + country - shown next to the bid
       amount input so the buyer picks a bid knowing what calls actually clear at.
       Anchors on real winning-bid percentiles; nothing here may be hardcoded copy.

       POPULATION, and why the copy below did NOT change when it narrowed: as of
       2026-09-04 the backend measures every price and estimate here over auctions a call
       ACTUALLY ARRIVED ON, not auctions merely won at a positive price. Winning an
       auction and receiving a call are different events, and a seller can win an auction
       and never route a call into it.

       The wording stays "winning bid" ON PURPOSE (owner decision). On this UI a winning
       bid MEANS a delivered call - that is the term's definition here, not a loose
       approximation of it - so renaming it to "calls that came through" would have
       introduced a second vocabulary for one concept and stranded every buyer, doc and
       support macro that already speaks the first. What changed is what the number is
       measured over; what it is called is unchanged.

       Consequence to keep in mind when editing: the population is SMALLER than it was,
       so these percentiles stand on fewer auctions than the same strings used to imply.
       See docs/MARKET_CONTEXT_DELIVERED_CALLS.md.

       The "last 7 days" strings below ARE deliberately hardcoded, and must stay that way.
       This panel reads E2 (market-context), whose window has always been a fixed rolling
       7 days. Only E1 (the Campaigns table's market-positions endpoint) has a window that
       varies by weekday and reports its length as windowHours; do not "fix" these strings
       into formatMarketWindow - that helper describes E1's window and would be wrong here.
       See docs/MARKET_CONTEXT_MONDAY_WINDOW.md. -->
  <div
    v-if="categoryMarketContext"
    class="bid-market-context"
    data-cy="bid-market-context"
  >
    <div class="d-flex align-items-center justify-content-between mb-3">
      <strong>Live market · {{ categoryName }}</strong>
      <span
        v-if="categoryMarketContext.marketActivity"
        class="text-muted bid-market-context__meta"
      >
        {{ getMarketActivityText(categoryMarketContext.marketActivity) }} · last 7 days
      </span>
    </div>

    <div class="bid-market-context__bar mb-2">
      <div
        class="bid-market-context__band"
        :style="bandStyle"
      />
      <div
        v-for="percentile in ticks"
        :key="percentile.label"
        class="bid-market-context__tick"
        :style="{ left: `${positionPercent(percentile.bid)}%` }"
      />
      <div
        v-if="currentBidNumber"
        class="bid-market-context__marker text-warning"
        :style="{ left: `${positionPercent(currentBidNumber)}%` }"
      />
    </div>

    <!-- The bar above carries the exact positions; these labels only have to stay
         readable, so they spread across the strip in percentile order rather than
         sitting at their own bid positions - at narrow widths, or with four-figure
         bids, positioned labels run into each other. -->
    <div class="bid-market-context__scale mb-2">
      <!-- When P25 through P75 land on one price there is no spread to lay out - three
           copies of the same number would just read as noise - so a flat market shows
           only the "9 in 10 winning bids were ... or less" line below.

           NO LONGER RARE. A flat strip used to need a genuinely single-priced market;
           now that the percentiles are measured only over delivered calls, the
           population behind them is smaller, and a thin cell can collapse all three
           quartiles onto one price with only a handful of calls behind it. So a flat
           strip, and the single "Suggested bid" card it pairs with below, can now
           present one confident-looking price that stands on four calls. A
           minimum-delivered-calls floor that hides the panel instead of collapsing to
           that card is tracked as an open follow-up in
           docs/MARKET_CONTEXT_DELIVERED_CALLS.md. -->
      <div
        v-if="!isFlatMarket"
        class="bid-market-context__scale-percentiles"
      >
        <span
          v-for="percentile in percentiles"
          :key="percentile.label"
          class="bid-market-context__scale-label text-muted"
        >
          {{ formatCurrency(percentile.bid) }} {{ percentile.label }}
        </span>
      </div>
      <div
        v-if="currentBidNumber"
        class="bid-market-context__scale-you"
      >
        <span
          class="bid-market-context__scale-label bid-market-context__scale-label--you text-warning"
          :style="youLabelStyle"
        >
          {{ formatCurrency(currentBidNumber) }} you
        </span>
      </div>
      <!-- The top of the market, in every market: the price 9 in 10 winning bids were at or
           below (winningBidP90InUSD), followed by the instruction to bid above it. That is
           the product's intent - push the buyer to lead the market, not to settle at the
           typical price - so the line deliberately does not say where the crowd clusters:
           a "typical price" sentence anchors bids low. It is a price a winner actually paid,
           and it is never below P75. In a flat market it reads "9 in 10 winning bids were $X
           or less" with $X the single clearing price, and the "Suggested bid" card below is
           that price plus the lead margin (R61: the Front-runner tier is P90 + $2.50; Entry
           and Competitive stay a few cents above their quartile). A line under the strip
           rather than a badge on a card, because the cards are priced above the market, at
           prices nobody won at. -->
      <div
        v-if="frontierWinningBid !== null"
        class="bid-market-context__scale-frontier text-muted"
        data-cy="market-frontier-line"
      >
        9 in 10 winning bids were {{ formatCurrency(frontierWinningBid) }} or less. Bid above it to lead this market.
      </div>
    </div>

    <b-form-text
      v-if="beatsPercent !== null"
      class="mb-3"
    >
      Your {{ formatCurrency(currentBidNumber) }} would have beaten about
      <strong>{{ beatsPercent }}%</strong> of winning bids in the last 7 days.
    </b-form-text>

    <!-- Every tier priced the same, so there is no ladder to choose from - one card with
         the single suggested price. Three identical cards would read as three options. The
         "9 in 10 winning bids were ... or less" line above already names the market's
         clearing price, so the card does not repeat it; the card is that price plus the
         lead margin (R61). -->
    <div
      v-if="singleTier"
      class="bid-market-context__tier mb-2"
      data-cy="tier-suggested-bid"
    >
      <div class="bid-market-context__tier-name text-muted">
        Suggested bid
      </div>
      <div class="bid-market-context__tier-bid">
        {{ formatCurrency(singleTier.bidInUSD) }}
      </div>
      <b-form-text class="mb-2 mt-0">
        {{ getTierEstimateText(singleTier) }}
      </b-form-text>
      <!-- Not `block` like the tier cards' buttons: those fill a narrow grid column, this
           card is the full width of the panel and a bar that wide reads as a banner -->
      <b-button
        variant="primary"
        size="sm"
        data-cy="button-use-tier-suggested-bid"
        @click="useTier(singleTier, 'suggested')"
      >
        Use {{ formatCurrency(singleTier.bidInUSD) }}
      </b-button>
    </div>

    <!-- The highlight, the badge and the primary button all follow isSuggested, the one tier
         the backend headlines. A backend older than that field sends none, and then nothing
         is highlighted rather than a guess. -->
    <b-row v-else>
      <b-col
        v-for="tier in displayTiers"
        :key="tier.key"
        :md="12 / displayTiers.length"
        class="mb-2"
      >
        <!-- A column with the button pushed to the bottom, so the buttons in the row line up
             however the badge and the estimate text above them wrap -->
        <div
          class="bid-market-context__tier d-flex flex-column"
          :class="{ 'bid-market-context__tier--highlighted': tier.isSuggested }"
          :data-cy="`tier-${tier.key.toLowerCase()}`"
        >
          <div class="bid-market-context__tier-name text-muted">
            {{ getTierText(tier.key) }}
            <b-badge
              v-if="tier.isSuggested"
              variant="primary"
              class="ml-1"
            >
              Suggested
            </b-badge>
          </div>
          <div class="bid-market-context__tier-bid">
            {{ formatCurrency(tier.bidInUSD) }}
          </div>
          <b-form-text class="mb-2 mt-0">
            {{ getTierEstimateText(tier) }}
          </b-form-text>
          <b-button
            :variant="tier.isSuggested ? 'primary' : 'outline-secondary'"
            size="sm"
            block
            class="mt-auto"
            :data-cy="`button-use-tier-${tier.key.toLowerCase()}`"
            @click="useTier(tier, tier.key.toLowerCase())"
          >
            Use {{ formatCurrency(tier.bidInUSD) }}
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-form-text>
      Estimates replay the last 7 days of auctions this campaign would have qualified for.
      Directional, not guaranteed.
    </b-form-text>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import formatCurrency from '@/utils/formatCurrency'
import { showErrorMessage } from '@/notification-utils'
import { USAGE_EVENTS } from '@/utils/usageEventKeys'
import { convertCurrencyStringToNumber } from '@/store/modules/campaign.store'
import { estimateBeatsPercent, getMarketActivityText, toCents } from '@/store/modules/market-context.store'

const tierTexts = {
  Entry: 'Entry',
  Competitive: 'Competitive',
  FrontRunner: 'Front-runner'
}

// How close to an end of the strip the "you" label has to get before it stops
// being centred on the marker and anchors to that edge instead
const EDGE_ANCHOR_PERCENT = 15

export default {
  name: 'BidMarketContextPanel',

  props: {
    category: {
      type: Object,
      default: null
    },

    countryId: {
      type: String,
      default: null
    },

    languageId: {
      type: String,
      default: null
    },

    // Comes straight from the currency input, so it can be a string like "38.00"
    currentBid: {
      type: [Number, String],
      default: null
    }
  },

  computed: {
    ...mapState('marketContext', ['categoryMarketContext']),

    categoryName() {
      return this.category?.name || ''
    },

    marketContextKey() {
      return `${this.category?.id || ''}:${this.countryId || ''}:${this.languageId || ''}`
    },

    currentBidNumber() {
      return convertCurrencyStringToNumber(this.currentBid) || null
    },

    percentiles() {
      return [
        { label: 'P25', bid: this.categoryMarketContext.winningBidP25InUSD },
        { label: 'P50', bid: this.categoryMarketContext.winningBidP50InUSD },
        { label: 'P75', bid: this.categoryMarketContext.winningBidP75InUSD }
      ]
    },

    // In a flat market all three percentiles sit on the same spot, where a tick
    // would only cut the clearing-price dot in half
    ticks() {
      return this.isFlatMarket ? [] : this.percentiles
    },

    beatsPercent() {
      return estimateBeatsPercent(this.currentBidNumber, this.categoryMarketContext)
    },

    // The strip spans half an interquartile range beyond P25/P75 on each side
    scale() {
      const p25 = this.categoryMarketContext.winningBidP25InUSD
      const p75 = this.categoryMarketContext.winningBidP75InUSD
      const padding = Math.max((p75 - p25) * 0.5, 1)

      return {
        min: p25 - padding,
        max: p75 + padding
      }
    },

    // Tracks the marker, except near the ends of the strip where a centred label
    // would hang outside the panel - there it anchors to the edge it is closest to
    youLabelStyle() {
      const percent = this.positionPercent(this.currentBidNumber)

      if (percent <= EDGE_ANCHOR_PERCENT) {
        return { left: 0 }
      }

      if (percent >= 100 - EDGE_ANCHOR_PERCENT) {
        return { right: 0 }
      }

      return { left: `${percent}%`, transform: 'translateX(-50%)' }
    },

    // One card per price. Tiers priced on the same cent collapse into one card: when two
    // percentiles coincide (P50 = P75 = $35 gives Competitive and Front-runner both $35.05),
    // two cards offering the same bid would read as two choices. The card kept is the
    // suggested tier, otherwise the higher one - tiers arrive in Entry, Competitive,
    // Front-runner order, so a later tier on the same cent is the higher one.
    //
    // Derived from the tiers themselves rather than from the percentiles (Entry and
    // Competitive are their quartile plus the step, Front-runner is P90 plus the lead
    // margin since R61 - see docs/MARKET_CONTEXT_WIRING.md). Reading the percentiles here
    // would mean a change to that mapping silently folds genuinely different bids into one
    // card, and a card is a bid the buyer can click.
    displayTiers() {
      const cardsByCents = new Map()

      for (const tier of this.categoryMarketContext.tiers || []) {
        const kept = cardsByCents.get(toCents(tier.bidInUSD))

        if (!kept?.isSuggested) {
          cardsByCents.set(toCents(tier.bidInUSD), tier)
        }
      }

      return [...cardsByCents.values()]
    },

    // The one card to render when every tier landed on the same cent, i.e. the market offers
    // a single price rather than a ladder; null whenever there is a real spread
    singleTier() {
      return this.displayTiers.length === 1 ? this.displayTiers[0] : null
    },

    // The price the "9 in 10 winning bids were ... or less" line names: P90 of the winning
    // bids (winningBidP90InUSD), the top of the market. It is the price the line tells the
    // buyer to bid above; it does not describe where the crowd clusters, which would anchor
    // bids low. The field ships with the backend release that added the line, so there is
    // no fallback: anything but a number, which is only an empty cell that already hides the
    // whole panel, hides the line.
    frontierWinningBid() {
      const p90 = this.categoryMarketContext.winningBidP90InUSD

      return typeof p90 === 'number' ? p90 : null
    },

    // True when P25 and P75 land on the same cent, i.e. the whole market cleared at one
    // price - what the strip and the scale line react to
    isFlatMarket() {
      const p25 = this.categoryMarketContext.winningBidP25InUSD
      const p75 = this.categoryMarketContext.winningBidP75InUSD

      return toCents(p25) === toCents(p75)
    },

    bandStyle() {
      // A flat market has no interquartile range to fill, and a zero-width band
      // reads as a missing bar - draw a dot on the clearing price instead
      if (this.isFlatMarket) {
        return {
          left: `${this.positionPercent(this.categoryMarketContext.winningBidP50InUSD)}%`,
          width: '10px',
          transform: 'translateX(-50%)'
        }
      }

      return {
        left: `${this.positionPercent(this.categoryMarketContext.winningBidP25InUSD)}%`,
        width: `${this.positionPercent(this.categoryMarketContext.winningBidP75InUSD) - this.positionPercent(this.categoryMarketContext.winningBidP25InUSD)}%`
      }
    },

    // The usage events' names for how the tiers are laid out
    usageLayout() {
      if (this.singleTier) {
        return 'single'
      }

      return this.isFlatMarket ? 'flat' : 'ladder'
    },

    // The tier the backend headlines, null from a backend that marks none
    suggestedTierBid() {
      return (this.categoryMarketContext.tiers || []).find(tier => tier.isSuggested)?.bidInUSD ?? null
    }
  },

  watch: {
    // One watcher on the combined key so a flow that sets category and country
    // in the same tick fires a single fetch instead of two racing ones
    marketContextKey: {
      immediate: true,
      handler() {
        this.loadMarketContext()
      }
    },

    // The usage event once the panel renders with data, once per category
    // (docs/MARKET_CONTEXT_USAGE_ANALYTICS.md)
    categoryMarketContext: {
      immediate: true,
      handler(context) {
        if (!context) {
          return
        }

        this.$trackOnce(USAGE_EVENTS.marketPanelShown.name, this.category?.id, {
          page: this.$route?.name,
          layout: this.usageLayout,
          ownBidInUSD: this.currentBidNumber,
          suggestedBidInUSD: this.suggestedTierBid,
          categoryId: this.category?.id
        })
      }
    }
  },

  methods: {
    ...mapActions('marketContext', ['fetchCategoryMarketContext', 'clearCategoryMarketContext']),

    formatCurrency,
    getMarketActivityText,

    getTierText(tierKey) {
      return tierTexts[tierKey] || tierKey
    },

    // A card's "Use $X". The host keeps the bid, and that the panel set it, for the save's usage event
    useTier(tier, usageTier) {
      this.$track(USAGE_EVENTS.marketPanelUseClicked.name, {
        page: this.$route?.name,
        tier: usageTier,
        previousBidInUSD: this.currentBidNumber,
        bidInUSD: tier.bidInUSD
      })

      this.$emit('select-bid', tier.bidInUSD, 'panel')
    },

    // A 1–1 range is the estimator's floor rather than a forecast: it carries no
    // more information than "about a call a day", and rendering a range whose ends
    // are the same number reads as a broken estimate. Drop it and leave the win
    // rate, which is still a real number at that volume.
    getTierEstimateText(tier) {
      const winRateText = `beats ~${tier.estimatedWinRatePercentage}% of winning bids`

      if (tier.estimatedCallsPerDayMin === 1 && tier.estimatedCallsPerDayMax === 1) {
        return winRateText
      }

      return `${winRateText} · est. ${tier.estimatedCallsPerDayMin}–${tier.estimatedCallsPerDayMax} calls/day`
    },

    positionPercent(value) {
      const percent = ((value - this.scale.min) / (this.scale.max - this.scale.min)) * 100
      return Math.min(100, Math.max(0, percent))
    },

    async loadMarketContext() {
      // The market cell is defined by all three of category + country + language, so
      // a partial selection would ask the API for a market that does not exist yet.
      // Clear instead, otherwise the previous cell's numbers stay on screen while the
      // buyer is halfway through changing the targeting.
      if (!this.category?.id || !this.countryId || !this.languageId) {
        this.clearCategoryMarketContext()
        return
      }

      try {
        await this.fetchCategoryMarketContext({
          categoryId: this.category.id,
          countryId: this.countryId,
          languageId: this.languageId
        })
      } catch (error) {
        // Market context is a helper, never a blocker - the bid input works without it
        showErrorMessage(error.response?.data?.title || 'Failed to load market context')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.bid-market-context {
  padding: 1rem;
  border: 1px solid $violet-light;
  border-radius: 0.25rem;
}

.bid-market-context__meta {
  font-size: 0.75rem;
}

.bid-market-context__bar {
  position: relative;
  height: 10px;
  border-radius: 5px;
  background-color: $violet-dark;
}

.bid-market-context__band {
  position: absolute;
  top: 0;
  height: 10px;
  border-radius: 5px;
  background-color: rgba($blue, 0.45);
}

.bid-market-context__tick {
  position: absolute;
  top: -5px;
  transform: translateX(-50%);
  width: 2px;
  height: 20px;
  background-color: $grey-lighter;
}

.bid-market-context__marker {
  position: absolute;
  top: -8px;
  transform: translateX(-50%);
  width: 3px;
  height: 26px;
  border-radius: 1px;
  // Inherits Bootstrap's text-warning color so it matches the "you" label
  background-color: currentColor;
}

.bid-market-context__scale-percentiles {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.bid-market-context__scale-frontier {
  font-size: 0.75rem;
  line-height: 1.125rem;
  text-align: center;
}

.bid-market-context__scale-label {
  font-size: 0.75rem;
  line-height: 1.125rem;
  white-space: nowrap;
}

// Its own row, tall enough to hold the label: the bid can land under any
// percentile, so sharing a line with the scale always risks an overlap
.bid-market-context__scale-you {
  position: relative;
  height: 1.125rem;
}

.bid-market-context__scale-label--you {
  position: absolute;
  top: 0;
}

.bid-market-context__tier {
  padding: 0.75rem;
  border: 1px solid $violet-light;
  border-radius: 0.25rem;
  height: 100%;
}

.bid-market-context__tier--highlighted {
  border-color: $blue;
}

.bid-market-context__tier-name {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.bid-market-context__tier-bid {
  font-size: 1.25rem;
  font-weight: 700;
}
</style>
